/** Persisted 3D viz payloads stored in `room_configs.viz_json`. Intentionally
 *  loose at the wire boundary — the admin modal/raytrace/volumetric response
 *  shapes are large and evolve; public share only needs to round-trip what
 *  SceneCanvas already painted. */
export interface PersistedVizPair<T = Record<string, unknown>> {
  before: T | null;
  after: T | null;
}

export interface PersistedVizPayloadV1 {
  version: 1;
  volume?: PersistedVizPair;
  raytrace?: PersistedVizPair;
  /** Before/after pair, same shape as `raytrace`/`volume` — brought to
   *  parity 2026-08-22 (see the simulation-persistence design spec, Section
   *  A). `modal` mode has no live before/after UI toggle today (it's an
   *  advanced-editor surface picker, not the result-page summary — see
   *  useModalViz.ts), so `before` is typically null in practice; the shape
   *  stays symmetric with the other two layers so round-tripping is uniform
   *  and a future before/after modal UI needs no wire-format change. */
  modal?: PersistedVizPair;
  /** Particle-velocity field before/after pair — same symmetric shape as the
   *  other viz layers so candidate caches can round-trip without a wire change. */
  velocity?: PersistedVizPair;
  /** ISO timestamp of the scan that produced this payload. */
  scannedAt?: string;
}

/** Job pipeline W2 (2026-09-06), §3.5: `room_configs.viz_json` v2. Bulk
 *  fields are ALWAYS artifact references (never inline cubes — see
 *  `ArtifactRef` in `adminTypes.ts`); volume/velocity are therefore tiny.
 *  No before/after pairs: `analysis.viz` (the reducer field this hydrates)
 *  is single-valued, matching the CURRENT scene — see editorState.ts's
 *  `AnalysisViz`. The before/after Compare toggle for 3D viz keeps working
 *  off its own live fetch (unchanged), which is not persisted here. */
export interface PersistedVizPayloadV2 {
  version: 2;
  modal?: Record<string, unknown> | null;
  volume?: Record<string, unknown> | null;
  velocity?: Record<string, unknown> | null;
  raytrace?: Record<string, unknown> | null;
  /** ISO timestamp of the scan that produced this payload. */
  scannedAt?: string;
}

export type PersistedVizPayload = PersistedVizPayloadV1 | PersistedVizPayloadV2;

export function isPersistedVizPayload(value: unknown): value is PersistedVizPayload {
  if (typeof value !== 'object' || value === null) return false;
  const version = (value as { version?: unknown }).version;
  return version === 1 || version === 2;
}

/** No-op today — v1 rows are read as-is (see `analysisFromResults`'s v1
 *  branch, which reads the `.after` side of each pair). Exists as the one
 *  named seam a future pass can fill in with an actual v1 -> v2 upgrade
 *  (e.g. lazily re-uploading an inline v1 cube as an artifact) without every
 *  call site needing to learn a new function name. */
export function migratePersistedViz(payload: PersistedVizPayload): PersistedVizPayload {
  return payload;
}

/** Build a versioned viz blob from in-memory hook state for PATCH / freeze. */
export function buildPersistedVizPayload(opts: {
  modal: { before: Record<string, unknown> | null; after: Record<string, unknown> | null };
  raytrace: { before: Record<string, unknown> | null; after: Record<string, unknown> | null };
  volume: { before: Record<string, unknown> | null; after: Record<string, unknown> | null };
  velocity?: { before: Record<string, unknown> | null; after: Record<string, unknown> | null };
  scannedAt?: string;
}): PersistedVizPayloadV1 | null {
  const hasModal = opts.modal.before != null || opts.modal.after != null;
  const hasRay = opts.raytrace.before != null || opts.raytrace.after != null;
  const hasVol = opts.volume.before != null || opts.volume.after != null;
  const velocity = opts.velocity ?? { before: null, after: null };
  const hasVelocity = velocity.before != null || velocity.after != null;
  if (!hasModal && !hasRay && !hasVol && !hasVelocity) return null;
  return {
    version: 1,
    ...(hasModal ? { modal: opts.modal } : {}),
    ...(hasRay ? { raytrace: opts.raytrace } : {}),
    ...(hasVol ? { volume: opts.volume } : {}),
    ...(hasVelocity ? { velocity } : {}),
    ...(opts.scannedAt ? { scannedAt: opts.scannedAt } : {}),
  };
}

/** v2 builder: single-valued (no before/after), matching `AnalysisViz` —
 *  see `editorState.ts`. Persist `analysis.viz` through this, never
 *  `buildPersistedVizPayload` (v1), so a saved room never re-acquires an
 *  inline cube in `viz_json`. */
export function buildPersistedVizPayloadV2(opts: {
  modal?: Record<string, unknown> | null;
  volume?: Record<string, unknown> | null;
  velocity?: Record<string, unknown> | null;
  raytrace?: Record<string, unknown> | null;
  scannedAt?: string;
}): PersistedVizPayloadV2 | null {
  const hasAny = opts.modal != null || opts.volume != null || opts.velocity != null || opts.raytrace != null;
  if (!hasAny) return null;
  return {
    version: 2,
    ...(opts.modal != null ? { modal: opts.modal } : {}),
    ...(opts.volume != null ? { volume: opts.volume } : {}),
    ...(opts.velocity != null ? { velocity: opts.velocity } : {}),
    ...(opts.raytrace != null ? { raytrace: opts.raytrace } : {}),
    ...(opts.scannedAt ? { scannedAt: opts.scannedAt } : {}),
  };
}

/** Single-valued viz extracted from either payload version — the shape
 *  `editorState.ts`'s `analysisFromResults` restores into `analysis.viz`.
 *  v1 takes the `.after` side of each pair (the treated/current-scene
 *  result — `before` was always the bare-room comparison, which is not
 *  what `analysis.viz` represents); v2 fields are already single-valued. */
export interface VizFields {
  modal: Record<string, unknown> | null;
  volume: Record<string, unknown> | null;
  velocity: Record<string, unknown> | null;
  raytrace: Record<string, unknown> | null;
}

export function vizFromPersisted(value: unknown): VizFields | null {
  if (!isPersistedVizPayload(value)) return null;
  if (value.version === 2) {
    const { modal = null, volume = null, velocity = null, raytrace = null } = value;
    if (modal == null && volume == null && velocity == null && raytrace == null) return null;
    return { modal, volume, velocity, raytrace };
  }
  const modal = value.modal?.after ?? null;
  const volume = value.volume?.after ?? null;
  const velocity = value.velocity?.after ?? null;
  const raytrace = value.raytrace?.after ?? null;
  if (modal == null && volume == null && velocity == null && raytrace == null) return null;
  return { modal, volume, velocity, raytrace };
}
